import {
  NON_RETURNABLE_ITEM_REASONS,
  ReturnableItem,
  ReturnProduct,
} from "@redotech/redo-return-core-common/draft-return/draft-return-items";
import type { InventorySettings } from "@redotech/redo-return-core-common/inventory-settings";
import { STEP_TYPE_NAMES } from "@redotech/redo-return-core-common/return-flow/step-type-names";
import { DraftReturn } from "./draft-return";
import { evaluatePreRejection } from "./pre-reject";
import { resolveResetFlow } from "./resolve-reset-flow";
import { getBundleGroupMemberIds } from "./return-together-key";
import { getPrimaryProduct } from "./util";

export enum LineItemReturnState {
  /** Nothing to say — the customer can return this today. */
  ELIGIBLE = "eligible",
  /** Every remaining unit walks the flow to a reject step. */
  BLOCKED = "blocked",
  PARTIALLY_BLOCKED = "partiallyBlocked",
  PRESET_SET = "presetSet",
  /** Never reaches the flow at all, so no preset can help. */
  NOT_RETURNABLE = "notReturnable",
}

interface VerdictBase {
  lineItemId: string;
  /** True when setting a preset here would not work, or would be rejected. */
  actionSuppressed: boolean;
}

/** Units that reject, out of the units on the row. */
interface BlockedCounts {
  blockedCount: number;
  totalCount: number;
}

/**
 * Each state carries only the fields it can answer for, so a row can't claim a
 * step name it has no preset for or a unit count it never counted.
 */
export type LineItemReturnVerdict = VerdictBase &
  (
    | { state: LineItemReturnState.ELIGIBLE }
    | { state: LineItemReturnState.PRESET_SET; presetStepName: string | null }
    | ({
        state: LineItemReturnState.BLOCKED;
        message: string | null;
      } & BlockedCounts)
    | ({
        state: LineItemReturnState.PARTIALLY_BLOCKED;
        message: string | null;
      } & BlockedCounts)
    | { state: LineItemReturnState.NOT_RETURNABLE; message: string }
  );

/**
 * NOT_FULFILLED is dropped because the feature is already fulfilled-gated, and
 * REJECTED duplicates a blocked row.
 */
const SURFACED_NON_RETURNABLE_REASONS: ReadonlySet<string> = new Set([
  NON_RETURNABLE_ITEM_REASONS.EXCLUDED_TAG,
  NON_RETURNABLE_ITEM_REASONS.EXCLUDED_BY_SETTINGS,
  NON_RETURNABLE_ITEM_REASONS.EXCLUDED_COLLECTION,
  NON_RETURNABLE_ITEM_REASONS.ALREADY_FULLY_RETURNED,
]);

/**
 * Reading maps the snapshot's string step types onto the symbols
 * `STEP_TYPE_NAMES` uses. Naming is cosmetic, so an unreadable flow is still preset.
 */
function getPresetStepName(
  reset: NonNullable<ReturnProduct["reset"]>,
  draftFlow: DraftReturn["flow"],
): string | null {
  try {
    const { flow, startStep, resetApplied } = resolveResetFlow({
      reset,
      draftFlow,
    });
    if (!resetApplied) {
      return null;
    }
    const step = flow.steps[startStep];
    if (!step) {
      return null;
    }
    const customTitle = "customTitle" in step ? step.customTitle : null;
    return customTitle || STEP_TYPE_NAMES[step.type] || null;
  } catch {
    return null;
  }
}

/**
 * Bundle components never inherit the parent's preset, and return-together
 * members must go back as a group, so a preset on a bundle row is inert.
 */
function isBundleInvolved(
  items: ReturnableItem[],
  draftReturn: DraftReturn,
): boolean {
  // Every unit of a row shares its bundle configuration, so one answers for all.
  const item = items[0];
  if (!item) {
    return false;
  }
  if (item.isExpanded || item.products.length > 1) {
    return true;
  }
  const groupIds = new Set(
    getBundleGroupMemberIds(item.id, draftReturn.returnableItems, {
      propertyNames: draftReturn.returnTogetherPropertyNames ?? [],
      useNativeBundles: draftReturn.useShopifyNativeBundles ?? false,
      bundlesEnabled: true,
    }),
  );
  // A group confined to one line item is just quantity — every unit of a
  // qty 5 row shares its grouping key — so it isn't a bundle.
  const lineItemIds = new Set(
    draftReturn.returnableItems
      .filter((member) => groupIds.has(member.id))
      .map((member) => member.lineItemId),
  );
  return lineItemIds.size > 1;
}

/**
 * Collapses per-unit pre-rejection into one verdict per Shopify line item,
 * which is the only granularity a preset can be written at.
 */
export function buildLineItemReturnVerdicts({
  draftReturn,
  showPreRejectedItems,
  orderHasReturn = false,
  inventorySettings,
}: {
  draftReturn: DraftReturn;
  showPreRejectedItems: boolean;
  orderHasReturn?: boolean;
  inventorySettings?: InventorySettings;
}): LineItemReturnVerdict[] {
  const verdicts: LineItemReturnVerdict[] = [];

  const itemsByLineItemId = new Map<string, ReturnableItem[]>();
  for (const item of draftReturn.returnableItems) {
    if (!item.lineItemId) {
      continue;
    }
    const units = itemsByLineItemId.get(item.lineItemId) ?? [];
    units.push(item);
    itemsByLineItemId.set(item.lineItemId, units);
  }

  for (const [lineItemId, items] of itemsByLineItemId) {
    const base = {
      lineItemId,
      actionSuppressed: isBundleInvolved(items, draftReturn) || orderHasReturn,
    };

    // The walk starts at the preset, so evaluate before trusting one — a preset
    // pointing at a step that still rejects has not unblocked anything.
    const blocked = items
      .map((item) =>
        evaluatePreRejection({
          returnableItem: item,
          draftReturn,
          showPreRejectedItems,
          inventorySettings,
        }),
      )
      .filter((rejection) => rejection.rejected);

    if (blocked.length === 0) {
      const preset = items
        .map((item) => getPrimaryProduct(item).reset)
        .find((reset) => reset != null);
      verdicts.push(
        preset
          ? {
              ...base,
              state: LineItemReturnState.PRESET_SET,
              presetStepName: getPresetStepName(preset, draftReturn.flow),
            }
          : { ...base, state: LineItemReturnState.ELIGIBLE },
      );
      continue;
    }

    // Units can be priced differently, so their reject copy can differ too;
    // the row reports the first one that rejected.
    const counts = { blockedCount: blocked.length, totalCount: items.length };
    const message = blocked[0]?.message ?? null;
    verdicts.push(
      blocked.length === items.length
        ? { ...base, state: LineItemReturnState.BLOCKED, message, ...counts }
        : {
            ...base,
            state: LineItemReturnState.PARTIALLY_BLOCKED,
            message,
            ...counts,
          },
    );
  }

  for (const item of draftReturn.nonReturnableItems ?? []) {
    if (itemsByLineItemId.has(item.lineItemId)) {
      continue;
    }
    const reason = item.reasons.find((candidate) =>
      SURFACED_NON_RETURNABLE_REASONS.has(candidate),
    );
    if (!reason) {
      continue;
    }
    verdicts.push({
      lineItemId: item.lineItemId,
      state: LineItemReturnState.NOT_RETURNABLE,
      message: reason,
      // A preset cannot unblock an item the flow never sees.
      actionSuppressed: true,
    });
  }

  return verdicts;
}
